/**
 * 左工具栏（M2-05）。内容完全由工具注册表生成——
 * 这里没有工具清单，只有渲染。激活态受控，切换逻辑在交互层的 ToolManager。
 *
 * 长按分组（Photoshop 惯例）：`registry.ts` 的 `TOOL_GROUPS` 声明了哪些工具
 * 挤在同一个槽位里（比如矩形/椭圆选框）。短按直接激活这个槽位当前记着的
 * 那个变体；按住不放 {@link LONG_PRESS_MS} 之后弹出同组其余变体供选择——
 * 跟 PS 的手感一致：不用先展开菜单才能用最近用过的那个变体。
 */
import type { JSX } from 'solid-js';
import { For, Show, createSignal, onCleanup, onMount } from 'solid-js';
import type {
  ToolDefinition,
  ToolGroup,
  ToolGroupMember,
  ToolOptionValue,
} from '../../interaction/tools/registry';
import { Icon, type IconName } from '../icons/Icon';
import styles from './Toolbar.module.css';

const LONG_PRESS_MS = 350;

export interface ToolbarProps {
  readonly tools: readonly ToolDefinition[];
  /** 长按分组表。省略时退化成纯平铺（每个工具一个按钮）。 */
  readonly groups?: readonly ToolGroup[];
  readonly activeId: string;
  readonly onSelect: (id: string) => void;
  /** 读工具当前选项值——用来判断分组按钮此刻该显示哪个变体的图标。 */
  readonly getOption?: (toolId: string, key: string) => ToolOptionValue;
  /** 选中分组里的某个变体：既要切工具，也要把变体声明的选项覆写上去。 */
  readonly onSelectMember?: (member: ToolGroupMember) => void;
  /** 前景/背景色的 CSS 颜色串（由装配层给，UI 不做颜色运算）。 */
  readonly foreground?: string | undefined;
  readonly background?: string | undefined;
  /** 点右上角交换箭头 = 交换前景/背景（X 键的鼠标等价，跟真实 PS 一致）。 */
  readonly onSwapColors?: (() => void) | undefined;
  /** 点大色块（前景）/小色块（背景）= 弹出取色器改这个颜色（跟真实 PS 一致）。 */
  readonly onEditForeground?: (() => void) | undefined;
  readonly onEditBackground?: (() => void) | undefined;
}

type ToolbarSlot =
  | { readonly kind: 'single'; readonly tool: ToolDefinition }
  | { readonly kind: 'group'; readonly group: ToolGroup };

/**
 * 按注册表原有顺序把工具列表折叠成槽位：属于同一个分组的工具只在
 * 分组第一个成员的位置出现一次（渲染成一个带 flyout 的槽位），
 * 分组引用到的其余工具 id 在原位置直接跳过——不会在列表后面又冒出来
 * 一个重复按钮。单变体分组（见 registry.ts 的注释）当作没有分组处理。
 */
function buildSlots(
  tools: readonly ToolDefinition[],
  groups: readonly ToolGroup[],
): readonly ToolbarSlot[] {
  const groupByFirstMember = new Map<string, ToolGroup>();
  const consumed = new Set<string>();
  for (const group of groups) {
    if (group.members.length < 2) continue;
    groupByFirstMember.set(group.members[0]!.toolId, group);
    for (const member of group.members) consumed.add(member.toolId);
  }
  const slots: ToolbarSlot[] = [];
  const seenGroup = new Set<string>();
  for (const tool of tools) {
    const group = groupByFirstMember.get(tool.id);
    if (group !== undefined) {
      if (seenGroup.has(group.id)) continue;
      seenGroup.add(group.id);
      slots.push({ kind: 'group', group });
      continue;
    }
    if (consumed.has(tool.id)) continue;
    slots.push({ kind: 'single', tool });
  }
  return slots;
}

/** 分组里哪个变体跟当前激活的工具+选项匹配。找不到返回 -1（交给记忆态兜底）。 */
function matchingMemberIndex(
  group: ToolGroup,
  activeId: string,
  getOption: ((toolId: string, key: string) => ToolOptionValue) | undefined,
): number {
  return group.members.findIndex((member) => {
    if (member.toolId !== activeId) return false;
    if (member.optionOverrides === undefined || getOption === undefined) return true;
    return Object.entries(member.optionOverrides).every(
      ([key, value]) => getOption(member.toolId, key) === value,
    );
  });
}

export function Toolbar(props: ToolbarProps): JSX.Element {
  const [openGroupId, setOpenGroupId] = createSignal<string | null>(null);
  const [remembered, setRemembered] = createSignal<Readonly<Record<string, number>>>({});
  let rootEl: HTMLDivElement | undefined;
  let pressTimer: ReturnType<typeof setTimeout> | undefined;
  let longPressFired = false;

  const closeFlyout = (): void => {
    setOpenGroupId(null);
  };

  const memberIndex = (group: ToolGroup): number => {
    const matched = matchingMemberIndex(group, props.activeId, props.getOption);
    return matched >= 0 ? matched : (remembered()[group.id] ?? 0);
  };

  const selectMember = (group: ToolGroup, index: number, member: ToolGroupMember): void => {
    setRemembered((prev) => ({ ...prev, [group.id]: index }));
    props.onSelectMember?.(member);
  };

  onMount(() => {
    // 点分组外的任何地方关闭 flyout；pointerdown 而非 click，拖拽开始也该关
    const onPointerDown = (event: PointerEvent): void => {
      if (rootEl !== undefined && event.target instanceof Node && !rootEl.contains(event.target)) {
        closeFlyout();
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeFlyout();
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    onCleanup(() => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
      clearTimeout(pressTimer);
    });
  });

  const slots = (): readonly ToolbarSlot[] => buildSlots(props.tools, props.groups ?? []);

  const renderSingle = (tool: ToolDefinition): JSX.Element => (
    <button
      type="button"
      class={styles.toolButton}
      classList={{ [styles.active!]: props.activeId === tool.id }}
      title={`${tool.label} (${tool.key})`}
      aria-label={tool.label}
      aria-pressed={props.activeId === tool.id}
      data-testid={`tool-${tool.id}`}
      onClick={() => {
        closeFlyout(); // 点了别的工具，之前长按弹出的子菜单不该还留着
        props.onSelect(tool.id);
      }}
    >
      <Icon name={tool.glyph as IconName} />
    </button>
  );

  const renderGroup = (group: ToolGroup): JSX.Element => {
    const current = (): ToolGroupMember => group.members[memberIndex(group)]!;
    return (
      <div class={styles.groupSlot}>
        <button
          type="button"
          class={styles.toolButton}
          classList={{ [styles.active!]: props.activeId === current().toolId }}
          title={`${current().label}（长按切换变体）`}
          aria-label={current().label}
          aria-haspopup="menu"
          aria-expanded={openGroupId() === group.id}
          aria-pressed={props.activeId === current().toolId}
          // 沿用未分组时代的 `tool-{id}` testid——group.id 就是原来的工具 id
          // （marquee/lasso/crop），换成别的字符串会悄悄破坏一批既有 e2e 用例
          data-testid={`tool-${group.id}`}
          onPointerDown={() => {
            // 按下的是别的槽位，先把上一个还开着的子菜单关掉——不然长按
            // A 弹出子菜单后，不点子菜单直接点 B 工具，A 的菜单会一直留着
            if (openGroupId() !== null && openGroupId() !== group.id) closeFlyout();
            longPressFired = false;
            clearTimeout(pressTimer);
            pressTimer = setTimeout(() => {
              longPressFired = true;
              setOpenGroupId(group.id);
            }, LONG_PRESS_MS);
          }}
          onPointerUp={() => {
            clearTimeout(pressTimer);
            if (longPressFired) return; // 长按已经弹出 flyout，交给用户点里面的项
            const index = memberIndex(group);
            selectMember(group, index, group.members[index]!);
          }}
          onPointerLeave={() => {
            clearTimeout(pressTimer);
          }}
        >
          <Icon name={current().glyph as IconName} />
          <span class={styles.groupMarker} aria-hidden="true" />
        </button>
        <Show when={openGroupId() === group.id}>
          <div class={styles.flyout} role="menu" data-testid={`toolbar-flyout-${group.id}`}>
            <For each={group.members}>
              {(member, index) => (
                <button
                  type="button"
                  role="menuitem"
                  class={styles.flyoutItem}
                  classList={{ [styles.flyoutItemActive!]: index() === memberIndex(group) }}
                  data-testid={`toolbar-flyout-item-${group.id}-${index()}`}
                  onClick={() => {
                    selectMember(group, index(), member);
                    closeFlyout();
                  }}
                >
                  <Icon name={member.glyph as IconName} />
                  <span>{member.label}</span>
                </button>
              )}
            </For>
          </div>
        </Show>
      </div>
    );
  };

  return (
    <div
      ref={(el) => {
        rootEl = el;
      }}
      class={styles.root}
    >
      <For each={slots()}>{(slot) => (slot.kind === 'single' ? renderSingle(slot.tool) : renderGroup(slot.group))}</For>
      <Show when={props.foreground !== undefined}>
        <div class={styles.swatches} data-testid="color-swatches">
          {/* 交换按钮：颜色走 currentColor（Icon.tsx 的 stroke 约定）+
              CSS token，不能像色块底色那样直接抄用户的前景/背景色——那两个
              颜色本身有可能是深色（比如纯黑），箭头会跟色块自己一样糊在
              一起看不见，跟数字输入框步进箭头之前那个问题是同一类坑。 */}
          <button
            type="button"
            class={styles.swapIcon}
            title="交换前景色与背景色（X）"
            aria-label="交换前景色与背景色"
            data-testid="color-swap"
            onClick={() => {
              closeFlyout();
              props.onSwapColors?.();
            }}
          >
            <Icon name="arrow-left-right" size={10} />
          </button>
          <button
            type="button"
            class={styles.swatchBack}
            title="背景色（点击修改）"
            aria-label="修改背景色"
            data-testid="color-background"
            style={{ background: props.background }}
            onClick={() => {
              closeFlyout();
              props.onEditBackground?.();
            }}
          />
          <button
            type="button"
            class={styles.swatchFront}
            title="前景色（点击修改）"
            aria-label="修改前景色"
            data-testid="color-foreground"
            style={{ background: props.foreground }}
            onClick={() => {
              closeFlyout();
              props.onEditForeground?.();
            }}
          />
        </div>
      </Show>
    </div>
  );
}
